import {
  Component,
  Inject,
  Input,
  OnChanges,
  PLATFORM_ID,
  SimpleChanges,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Colors } from '../../../shared/enums/shared.enums';

import { ImageComponent } from '../../atoms/image/image.component';
import { CaasComponent } from '../../../shared/interfaces/caas/caas.interface';
import { ImagesService } from '../../../services/images.service';
import { ErrorHandlerService } from '../../../services/error-handler.service';
import { safeAssign } from '../../../shared/utils/safe-assign.util';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';

@Component({
  selector: 'hdi-static-graphic',
  standalone: true,
  imports: [CommonModule, ImageComponent, NgxSkeletonLoaderModule],
  templateUrl: './static-graphic.component.html',
  styleUrls: ['./static-graphic.component.scss'],
  host: {
    id: crypto.randomUUID().toString(),
  },
})
export class StaticGraphicComponent implements OnChanges {
  @Input() src: string | string[] = '';
  @Input() alt: string = '';
  @Input() title: string = '';
  @Input() headline?: string;

  @Input() backgroundColor?: Colors;
  @Input() caasData?: CaasComponent;

  desktopImageSrc: string = '';
  mobileImageSrc: string = '';

  public isLoading = true;

  constructor(
    private readonly imagesService: ImagesService,
    private readonly errorHandler: ErrorHandlerService,
    @Inject(PLATFORM_ID) private readonly platformId: object
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['caasData']) {
      // Set desktop and mobile image sources
      safeAssign(
        () => {
          this.desktopImageSrc =
            this.caasData?.formData?.st_image?.value?.url ?? '';
        },
        'Assigning desktopImageSrc',
        this.errorHandler
      );
      safeAssign(
        () => {
          this.mobileImageSrc =
            this.caasData?.formData?.st_image_mobile?.value?.url ||
            this.desktopImageSrc;
        },
        'Assigning mobileImageSrc',
        this.errorHandler
      );
      // Set the headline, alt text, and title
      safeAssign(
        () => {
          this.headline = this.caasData?.formData?.st_header?.value ?? '';
        },
        'Assigning headline',
        this.errorHandler
      );
      safeAssign(
        () => {
          if (this.caasData?.formData?.st_image?.value?.identifier) {
            this.alt =
              this.imagesService.getAltTextById(
                this.caasData.formData.st_image.value.identifier
              ) ?? '';
          }
        },
        'Assigning alt text',
        this.errorHandler
      );
      safeAssign(
        () => {
          if (this.caasData?.formData?.st_image_title?.value) {
            this.title = this.caasData.formData.st_image_title.value;
          }
        },
        'Assigning title',
        this.errorHandler
      );
      if (isPlatformBrowser(this.platformId)) {
        this.isLoading = false;
      }
    }
  }

  isTransparent(): boolean {
    return this.backgroundColor === undefined;
  }

  isArrayOfImages(): boolean {
    return Array.isArray(this.src);
  }

  getImageSources(): string[] {
    return Array.isArray(this.src) ? this.src : [this.src];
  }
}
